<template>
    <LoadingCard v-if="loading" />
    <div v-else>
        <div class="card">
            <div class="card-header bg-primary text-white">
                Processors
            </div>
            <b-table small hover stacked="md" :items="filteredData" :fields="table.fields" sort-by="name" :show-empty="true">
                <template slot="name" slot-scope="row">
                    <div class="custom-control custom-checkbox"
                         v-b-tooltip="{
                            placement: 'left',
                            trigger: 'hover',
                            title: row.item.isGlobalLink ? 'Processor linked at setup level' : ''
                         }"
                         @click.prevent="toggleRow(row.item)">
                        <input class="custom-control-input"
                               :id="`processor${row.item.id}`" type="checkbox"
                               :checked="row.item.isLocalLink || row.item.isGlobalLink"
                               :disabled=" row.item.isGlobalLink"
                        >
                        <label class="custom-control-label" :for="`processor${row.item.id}`">{{ row.item.name }}</label>
                    </div>
                </template>
                <template slot="is_exclusion" slot-scope="row">
                    <div class="custom-control custom-checkbox" v-if="row.item.isGlobalLink">
                        <input class="custom-control-input"
                               :id="`processor${row.item.id}_exclusion`" type="checkbox"
                               :checked="row.item.isExclusion"
                               @click.prevent="toggleExclusionRow(row.item)"
                        >
                        <label class="custom-control-label" :for="`processor${row.item.id}_exclusion`">
                            {{ row.item.isExclusion ? 'Block access disallowed' : 'Disallow block access' }}
                        </label>
                    </div>
                </template>
            </b-table>
        </div>
    </div>
</template>
<script>
import createLinkMutation from '@views/Blocks/ProcessorManager/mutationCreateGrowerProcessorLink.graphql';
import deleteLinkMutation from '@views/Blocks/ProcessorManager/mutationDeleteGrowerProcessorLink.graphql';
import listLinkedProcessorsQuery from '@views/Blocks/ProcessorManager/queryListGrowerLinkedProcessors.graphql';
import listProcessorsQuery from '@views/Blocks/ProcessorManager/queryListProcessors.graphql';

/**
 * @property {String} searchTerm
 */
export default {
  name: 'ProcessorManager',
  props: ['block'],
  apollo: {
    allProcessors: {
      query: listProcessorsQuery,
      update: data => data.filterProcessors.data,
    },
    linkedProcessors: {
      query: listLinkedProcessorsQuery,
      variables() {
        return {
          input: {
            block_id: [this.block.id],
          },
        };
      },
      update: data => data.filterGrowerProcessorLinks,
    },
  },
  data() {
    return {
      allProcessors: [],
      linkedProcessors: [],
      loading: 0,
    };
  },
  computed: {
    table() {
      return {
        fields: [
          { key: 'name', label: 'Processor', sortable: true },
          { key: 'is_exclusion', label: 'Exclude' },
        ],
      };
    },
    filteredData() {
      return this.allProcessors.map(processor => ({
        ...processor,
        isGlobalLink: this.linkedGlobally(processor.id),
        isLocalLink: this.linkedLocally(processor.id),
        isExclusion: this.linkedExclusion(processor.id),
      }));
    },
  },
  mounted() {
  },
  methods: {
    linkedGlobally(processorId) {
      return this.linkedProcessors.some(
        link => link.processor.id === processorId && link.block === null,
      );
    },
    linkedLocally(processorId) {
      return this.linkedProcessors.some(
        link =>
          link.processor.id === processorId &&
          this.$path.get(link, 'block.id') === this.block.id,
      );
    },
    linkedAtAll(processorId) {
      return this.linkedProcessors.some(
        link => link.processor.id === processorId,
      );
    },
    linkedExclusion(processorId) {
      const link = this.linkedProcessors.find(
        link => link.processor.id === processorId
          && this.$path.get(link, 'block.id') === this.block.id
          && link.is_exclusion,
      );

      return link;
    },
    toggleRow(item) {
      if (!this.linkedGlobally(item.id)) {
        this.handleProcessorToggle(item);
      }
    },
    toggleExclusionRow(item) {
      if (this.linkedGlobally(item.id)) {
        this.handleProcessorExclusionToggle(item);
      }
    },
    handleProcessorToggle(processor) {
      if (this.linkedAtAll(processor.id)) {
        this.unlinkProcessor(processor);
      } else {
        this.linkProcessor(processor);
      }
    },
    handleProcessorExclusionToggle(processor) {

      if (this.linkedExclusion(processor.id)) {
        this.unlinkProcessor(processor);
      } else {
        this.linkProcessor(processor, true);
      }
    },
    unlinkProcessor(processor) {
      const linkToRemove = this.linkedProcessors.find(
        link =>
          link.processor.id === processor.id &&
          this.$path.get(link, 'block.id') === this.block.id,
      );

      this.$apollo.mutate({
        mutation: deleteLinkMutation,
        variables: {
          input: {
            processor_id: processor.id,
            block_id: this.block.id,
          },
        },
        update: (store, { data: { deleteGrowerProcessorLink: removedLink } }) => {
          const queryKey = {
            query: listLinkedProcessorsQuery,
            variables: {
              input: {
                block_id: [this.block.id],
              },
            },
          };
          const data = store.readQuery(queryKey);
          const indexToRemove = data.filterGrowerProcessorLinks.findIndex(
            item => item.id === removedLink.id,
          );
          if (indexToRemove !== -1) {
            data.filterGrowerProcessorLinks.splice(indexToRemove, 1);
            store.writeQuery({ ...queryKey, data });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteGrowerProcessorLink: {
            ...linkToRemove,
          },
        },
      });
    },
    linkProcessor(processor, exclusion = false) {
      this.$apollo.mutate({
        mutation: createLinkMutation,
        variables: {
          input: {
            processor_id: processor.id,
            block_id: this.block.id,
            is_exclusion: exclusion,
          },
        },
        update: (store, { data: { createGrowerProcessorLink: link } }) => {
          const queryKey = {
            query: listLinkedProcessorsQuery,
            variables: {
              input: {
                block_id: [this.block.id],
              },
            },
          };
          const data = store.readQuery(queryKey);
          data.filterGrowerProcessorLinks.push(link);
          store.writeQuery({ ...queryKey, data });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createGrowerProcessorLink: {
            __typename: 'GrowerProcessorLink',
            id: -1,
            is_exclusion: exclusion,
            processor: processor,
            block: { ...this.block },
          },
        },
      });
    },
  },
};
</script>
